import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Panel, AdminHeader, Stat, Field, SelectField } from "@/pages/admin/_shared";
import { Crown, Trash, Lightning, Plus, XCircle as TicketX } from "@phosphor-icons/react";

export default function AdminSubscriptions() {
  const [subs, setSubs] = useState([]);
  const [promos, setPromos] = useState([]);
  const [filter, setFilter] = useState("");
  const [overview, setOverview] = useState(null);
  const [newPromo, setNewPromo] = useState({ code: "", percent_off: 25, max_uses: 100, tier: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, p, o] = await Promise.all([
      api.get(`/admin/subscriptions${filter ? `?status=${filter}` : ""}`),
      api.get("/admin/promo-codes"),
      api.get("/admin/overview"),
    ]);
    setSubs(s.data);
    setPromos(p.data);
    setOverview(o.data);
  };
  useEffect(() => { load(); }, [filter]);

  const change = async (user_id, new_tier) => {
    await api.post(`/admin/subscriptions/${user_id}/change-tier`, { new_tier });
    load();
  };
  const cancel = async (user_id) => {
    if (!window.confirm("Cancel this user's subscription?")) return;
    await api.post(`/admin/subscriptions/${user_id}/cancel`);
    load();
  };
  const createPromo = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/promo-codes", { ...newPromo, tier: newPromo.tier || null });
      setNewPromo({ code: "", percent_off: 25, max_uses: 100, tier: "" });
      load();
    } finally { setBusy(false); }
  };
  const deletePromo = async (id) => {
    if (!window.confirm("Delete this promo code?")) return;
    await api.delete(`/admin/promo-codes/${id}`);
    load();
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-subscriptions">
      <AdminHeader section="SUBSCRIPTIONS" title={["MANAGE ", "*REVENUE."]} />

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900 mb-8">
          <Stat label="MRR" value={overview.monthly_revenue_display} Icon={Crown} testid="sub-stat-mrr" />
          <Stat label="ACTIVE" value={overview.active_subscriptions} Icon={Lightning} testid="sub-stat-active" />
          <Stat label="PENDING" value={overview.pending_subscriptions} testid="sub-stat-pending" />
          <Stat label="CANCELED" value={overview.canceled_subscriptions} testid="sub-stat-canceled" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Promo codes */}
        <Panel title="PROMO CODES" tag={`${promos.length}`} testid="promo-panel">
          <form onSubmit={createPromo} className="mb-5 space-y-2">
            <Field label="Code" value={newPromo.code} onChange={(v) => setNewPromo({ ...newPromo, code: v.toUpperCase() })} placeholder="TRAPSZN50" testid="promo-code" required />
            <div className="grid grid-cols-2 gap-2">
              <Field label="% off" type="number" value={newPromo.percent_off} onChange={(v) => setNewPromo({ ...newPromo, percent_off: parseInt(v || 0) })} testid="promo-percent" />
              <Field label="Max uses" type="number" value={newPromo.max_uses} onChange={(v) => setNewPromo({ ...newPromo, max_uses: parseInt(v || 0) })} testid="promo-max" />
            </div>
            <SelectField label="Tier (optional)" value={newPromo.tier} onChange={(v) => setNewPromo({ ...newPromo, tier: v })} options={[{ value: "", label: "ALL TIERS" }, { value: "standard", label: "STANDARD" }, { value: "intermediate", label: "INTERMEDIATE" }, { value: "pro", label: "PRO" }]} testid="promo-tier" />
            <button type="submit" disabled={busy || !newPromo.code} data-testid="promo-submit" className="w-full bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold py-2.5 hover:bg-white disabled:opacity-50 mt-2 inline-flex items-center justify-center gap-2">
              <Plus size={12} weight="bold" /> CREATE CODE
            </button>
          </form>
          <ul className="space-y-2 max-h-[280px] overflow-y-auto">
            {promos.map((p) => (
              <li key={p.id} className="border border-zinc-900 p-2.5 bg-black/40 flex items-center gap-2" data-testid={`promo-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm tracking-tight">{p.code}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-ink-dim mt-0.5">
                    -{p.percent_off}% · {p.uses}/{p.max_uses} used {p.tier && `· ${p.tier.toUpperCase()}`}
                  </div>
                </div>
                {p.active ? (
                  <span className="text-[9px] tracking-[0.3em] uppercase text-brand-green">ACTIVE</span>
                ) : (
                  <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">INACTIVE</span>
                )}
                <button onClick={() => deletePromo(p.id)} className="text-ink-mute hover:text-brand-red p-1" data-testid={`promo-del-${p.id}`}>
                  <Trash size={12} weight="bold" />
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        {/* Subscribers table */}
        <Panel title="SUBSCRIBERS" tag={`${subs.length}`} className="lg:col-span-2" testid="subs-list-panel">
          <div className="flex gap-1 mb-4">
            {["", "active", "pending", "canceled"].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setFilter(s)}
                data-testid={`sub-filter-${s || "all"}`}
                className={`text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 border transition-colors ${
                  filter === s ? "border-brand-red text-brand-red" : "border-zinc-800 text-ink-mute hover:border-zinc-600"
                }`}
              >
                {s || "ALL"}
              </button>
            ))}
          </div>
          {subs.length === 0 ? (
            <div className="text-xs text-ink-dim text-center py-10">No subscribers in this filter.</div>
          ) : (
            <ul className="divide-y divide-zinc-900 max-h-[420px] overflow-y-auto">
              {subs.map((s, i) => (
                <li key={i} className="py-3 flex items-center gap-3" data-testid={`sub-row-${s.user_id}`}>
                  <div className={`w-1 self-stretch ${s.status === "active" ? "bg-brand-green" : s.status === "canceled" ? "bg-brand-red" : "bg-brand-purple"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold tracking-tight text-sm truncate">
                      {s.user?.producer_name || "—"}
                    </div>
                    <div className="text-[10px] text-ink-dim truncate">{s.user?.email}</div>
                  </div>
                  <span className={`text-[10px] tracking-[0.25em] uppercase px-2 py-1 border ${
                    s.tier === "pro" ? "border-brand-purple text-brand-purple"
                    : s.tier === "intermediate" ? "border-brand-red text-brand-red"
                    : "border-zinc-700 text-ink-mute"
                  }`}>{s.tier}</span>
                  <select
                    value={s.tier}
                    onChange={(e) => change(s.user_id, e.target.value)}
                    data-testid={`sub-tier-${s.user_id}`}
                    className="bg-black border border-zinc-800 px-2 py-1 text-[10px] tracking-[0.2em] uppercase font-mono"
                  >
                    <option value="standard">STANDARD</option>
                    <option value="intermediate">INTERMEDIATE</option>
                    <option value="pro">PRO</option>
                  </select>
                  <button onClick={() => cancel(s.user_id)} data-testid={`sub-cancel-${s.user_id}`} className="text-ink-mute hover:text-brand-red p-2" title="Cancel">
                    <TicketX size={14} weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
